import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { computed, flow, observable, runInAction } from "mobx";
import { BaseOptions } from "@devowl-wp/utils";
import { TcfDataCategory } from "../models/tcfDataCategory.js";
import { TcfFeature } from "../models/tcfFeature.js";
import { TcfPurpose } from "../models/tcfPurpose.js";
import { TcfVendor } from "../models/tcfVendor.js";
import { TcfVendorConfigurationCollection } from "../models/tcfVendorConfigurationCollection.js";
import { request } from "../utils/request.js";
import { locationRestTcfDeclarationsGet } from "../wp-api/tcfDeclarations.get.js";
import { locationRestTcfGvlPut } from "../wp-api/tcfGvl.put.js";
import { locationRestTcfVendorsGet } from "../wp-api/tcfVendors.get.js";
class TcfStore extends BaseOptions {
    get vendorConfigurationCount() {
        return this.fetchedAllVendorConfigurations ? this.vendorConfigurations.entries.size : this.rootStore.optionStore.allTcfVendorConfigurationCount;
    }
    constructor(rootStore){
        super();
        this.busyGvl = false;
        this.busyVendors = false;
        this.busyDeclarations = false;
        this.fetchedAllVendorConfigurations = false;
        this.vendors = new Map();
        this.purposes = new Map();
        this.specialPurposes = new Map();
        this.features = new Map();
        this.specialFeatures = new Map();
        this.dataCategories = new Map();
        this.fetchVendorConfigurations = flow(function*() {
            // In our UI, we do not have pagination, yet, so load all
            const perPage = 100;
            const pages = Math.ceil(this.vendorConfigurationCount / perPage);
            for(let i = 0; i < pages; i++){
                yield this.vendorConfigurations.get({
                    request: {
                        status: [
                            "draft",
                            "publish",
                            "private"
                        ]
                    },
                    params: {
                        offset: i * perPage,
                        per_page: 100,
                        context: "edit"
                    }
                });
            }
            this.fetchedAllVendorConfigurations = true;
        });
        this.fetchVendors = flow(function*() {
            this.busyVendors = true;
            try {
                const { vendorListVersion, vendors } = yield request({
                    location: locationRestTcfVendorsGet
                });
                for (const vid of Object.keys(vendors)){
                    this.vendors.set(vid, new TcfVendor(vendors[vid], this));
                }
                this.vendorListVersion = vendorListVersion;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyVendors = false;
            }
        });
        this.fetchDeclarations = flow(function*() {
            this.busyDeclarations = true;
            try {
                const { gvlSpecificationVersion, tcfPolicyVersion, purposes, specialPurposes, features, specialFeatures, dataCategories } = yield request({
                    location: locationRestTcfDeclarationsGet
                });
                for (const pid of Object.keys(purposes)){
                    this.purposes.set(pid, new TcfPurpose(purposes[pid], false, this));
                }
                for (const pid of Object.keys(specialPurposes)){
                    this.specialPurposes.set(pid, new TcfPurpose(specialPurposes[pid], true, this));
                }
                for (const pid of Object.keys(features)){
                    this.features.set(pid, new TcfFeature(features[pid], false, this));
                }
                for (const pid of Object.keys(specialFeatures)){
                    this.specialFeatures.set(pid, new TcfFeature(specialFeatures[pid], true, this));
                }
                for (const pid of Object.keys(dataCategories)){
                    this.dataCategories.set(pid, new TcfDataCategory(dataCategories[pid], this));
                }
                this.declarations = {
                    purposes,
                    specialPurposes,
                    features,
                    specialFeatures,
                    dataCategories
                };
                this.gvlSpecificationVersion = gvlSpecificationVersion;
                this.tcfPolicyVersion = tcfPolicyVersion;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyDeclarations = false;
            }
        });
        this.updateGvl = flow(function*() {
            this.busyGvl = true;
            try {
                const { gvlDownloadTime } = yield request({
                    location: locationRestTcfGvlPut
                });
                this.rootStore.optionStore.tcfGvlDownloadTime = gvlDownloadTime;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyGvl = false;
            }
        });
        this.rootStore = rootStore;
        runInAction(()=>{
            this.vendorConfigurations = new TcfVendorConfigurationCollection(this);
        });
    }
}
_ts_decorate([
    observable
], TcfStore.prototype, "busyGvl", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "busyVendors", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "busyDeclarations", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof TcfVendorConfigurationCollection === "undefined" ? Object : TcfVendorConfigurationCollection)
], TcfStore.prototype, "vendorConfigurations", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "fetchedAllVendorConfigurations", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TcfStore.prototype, "vendorListVersion", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TcfStore.prototype, "gvlSpecificationVersion", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], TcfStore.prototype, "tcfPolicyVersion", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Omit === "undefined" ? Object : Omit)
], TcfStore.prototype, "declarations", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "vendors", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "purposes", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "specialPurposes", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "features", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "specialFeatures", void 0);
_ts_decorate([
    observable
], TcfStore.prototype, "dataCategories", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], TcfStore.prototype, "vendorConfigurationCount", null);
export { TcfStore };
